import React, { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';
import { alpha, css, styled } from '@mui/material/styles';

import IconWithCaption from '~/blocks/TestCard/IconWithCaption';
import IKImage from '~/components/IKImage/IKImage';
import { createAnswersThemes } from '~/components/Test/OrderingQuestion/styles';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { MIDDLE_RADIUS_PX, SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import AnimatedCheckmark from '~/ui/components/AnimatedCheckmark/AnimatedCheckmark';

interface TestCardProp {
  children: ReactNode;
  title?: ReactNode;
  subtitle?: string;
  image?: string | null;
  className?: string;
}

export enum QuizColorsMode {
  PostVideo,
  CaseStudy,
}

type CompoundComponents = {
  RegularAnswer: typeof RegularAnswer;
  SmallAnswer: typeof SmallAnswer;
  AnswerChar: typeof AnswerChar;
  RegularTitle: typeof RegularTitle;
  SmallTitle: typeof SmallTitle;
  CorrectBadge: typeof CorrectAnswerBadge;
  IncorrectBadge: typeof IncorrectAnswerBadge;
};

const TestCard: React.FC<TestCardProp> & CompoundComponents = ({
  title,
  subtitle,
  image,
  children,
  ...rest
}) => (
  <Wrapper {...rest}>
    {!!title && title}
    {!!subtitle && <Subtitle>{subtitle}</Subtitle>}
    {Boolean(image) && (
      <ImgWrapper>
        <Image path={image} />
      </ImgWrapper>
    )}
    <Answers>{children}</Answers>
  </Wrapper>
);

export default TestCard;

const Wrapper = styled(Box)`
  margin-bottom: auto;
`;
const RegularTitle = styled(Typography)`
  display: flex;
`;
const SmallTitle = styled(RegularTitle)`
  font-size: 18px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 18px;
  }
`;
const Subtitle = styled(Typography)`
  font-size: 16px;
  font-weight: 700;
  margin: 10px 0 10px 24px;
  color: ${({ theme }) => theme.palette.common.gray};
`;
const ImgWrapper = styled('div')`
  display: flex;
  justify-content: center;
  width: 100%;
  position: relative;
  margin-top: ${TINY_MARGIN_PX};
`;
const Image = styled(IKImage)`
  max-width: 100%;
  min-width: 100%;
  border-radius: ${MIDDLE_RADIUS_PX};
  filter: drop-shadow(
    0px 3px 6px ${({ theme }) => alpha(theme.palette.common.blackPure, 0.501961)}
  );

  ${({ theme }) => theme.breakpoints.up('sm')} {
    min-width: 75%;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    min-width: 50%;
  }
`;
const Answers = styled('ul')`
  list-style: none;
  margin-top: ${SMALL_MARGIN_PX};
  padding: 0;
`;
export const AnswerChar = styled('span')`
  font-weight: 700;
  margin-right: 10px;
`;
export type RegularAnswerProps = {
  selected?: boolean;
  passed?: boolean;
  failed?: boolean;
  finished?: boolean;
  colorsMode?: QuizColorsMode;
};
const canBeSelected = (props: RegularAnswerProps) =>
  !props.finished && !props.passed && !props.failed;
export const BaseAnswer = styled('li', {
  shouldForwardProp: (prop) =>
    prop !== 'selected' &&
    prop !== 'passed' &&
    prop !== 'failed' &&
    prop !== 'finished' &&
    prop !== 'colorsMode',
})<RegularAnswerProps>`
  display: flex;
  align-items: center;
  background: ${({ theme, selected, finished, colorsMode = QuizColorsMode.PostVideo }) =>
    selected
      ? finished
        ? createAnswersThemes(theme)[colorsMode].selectedSubmitted
        : createAnswersThemes(theme)[colorsMode].selected
      : createAnswersThemes(theme)[colorsMode].regular};
  border: 2px solid
    ${({ selected, passed, failed, theme }) =>
      passed
        ? theme.palette.common.successOptional
        : failed
        ? theme.palette.common.status.error
        : selected
        ? theme.palette.common.card.accent.bd
        : theme.palette.common.blockBackground.lightBorder};
  cursor: ${(props) => canBeSelected(props) && 'pointer'};
  transition: 0.3s background-color, 0.3s border, 0.3s box-shadow;

  &:hover {
    ${({ theme, colorsMode = QuizColorsMode.PostVideo, ...rest }) =>
      !rest.selected &&
      canBeSelected(rest) &&
      css`
        background: ${createAnswersThemes(theme)[colorsMode].selected};
        border-color: ${createAnswersThemes(theme)[colorsMode].selected};
        box-shadow: ${theme.palette.common.card.shadowValue};
      `}
  }
`;
const RegularAnswer = styled(BaseAnswer)`
  min-height: 55px;
  border-radius: 5px;
  font-size: 18px;
  line-height: 1.5;
  margin-bottom: ${TINY_MARGIN_PX};
  padding: 4px ${SUBMIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.down('lg')} {
    font-size: 16px;
    padding: 4px 10px;
  }
`;
const SmallAnswer = styled(RegularAnswer)`
  font-size: 14px;
`;
const CorrectAnswerBadge = ({ className = '' }) => {
  const { t } = useTenantTranslation();
  return (
    <IconWithCaption
      className={className}
      correct
      icon={<AnimatedCheckmark correct={true} />}
      caption={t('page.video.correct')}
    />
  );
};
const IncorrectAnswerBadge = ({ className = '' }) => {
  const { t } = useTenantTranslation();
  return (
    <IconWithCaption
      className={className}
      icon={<AnimatedCheckmark correct={false} />}
      caption={t('page.video.incorrect')}
    />
  );
};

TestCard.RegularAnswer = RegularAnswer;
TestCard.SmallAnswer = SmallAnswer;
TestCard.AnswerChar = AnswerChar;
TestCard.RegularTitle = RegularTitle;
TestCard.SmallTitle = SmallTitle;
TestCard.CorrectBadge = CorrectAnswerBadge;
TestCard.IncorrectBadge = IncorrectAnswerBadge;
